import { Gatekeeper } from "./adapters/gatekeeper";
import { Session } from "./adapters/session";
import { TokenDecoder } from "./adapters/tokenDecoder";
import { Repository } from "./adapters/repository";
import { Inviters } from "./adapters/inviters";

import * as authenticate from "./useCases/authenticate";
import * as logOut from "./useCases/logOut";
import * as createAccount from "./useCases/createAccount";
import * as confirmAccountCreation from "./useCases/confirmAccountCreation";
import * as editUserPreferences from "./useCases/editUserPreferences";
import * as retrieveUserPreferences from "./useCases/retrieveUserPreferences";
import * as retrievePersonalWeeklyCalendar from "./useCases/retrievePersonalWeeklyCalendar";
import * as editPersonalWeeklyCalendar from "./useCases/editPersonalWeeklyCalendar";
import * as changePassword from "./useCases/changePassword";
import * as inviteMembers from "./useCases/inviteMembers";
import * as fillAccount from "./useCases/fillAccount";
import * as assignPersonalCalendarHolidays from "./useCases/assignPersonalCalendarHolidays";
import * as retrievePersonalCalendarHolidays from "./useCases/retrievePersonalCalendarHolidays";
import * as unAssignPersonalCalendarHolidays from "./useCases/unAssignPersonalCalendarHolidays";

enum UseCases {
  authenticate = "authenticate",
  logOut = "logOut",
  createAccount = "createAccount",
  confirmAccountCreation = "confirmAccountCreation",
  editUserPreferences = "editUserPreferences",
  retrieveUserPreferences = "retrieveUserPreferences",
  retrievePersonalWeeklyCalendar = "retrievePersonalWeeklyCalendar",
  editPersonalWeeklyCalendar = "editPersonalWeeklyCalendar",
  changePassword = "changePassword",
  inviteMembers = "inviteMembers",
  fillAccount = "fillAccount",
  assignPersonalCalendarHolidays = "assignPersonalCalendarHolidays",
  retrievePersonalCalendarHolidays = "retrievePersonalCalendarHolidays",
  unAssignPersonalCalendarHolidays = "unAssignPersonalCalendarHolidays",
}

interface Core {
  [UseCases.authenticate]: authenticate.Interactor;
  [UseCases.logOut]: logOut.Interactor;
  [UseCases.createAccount]: createAccount.Interactor;
  [UseCases.confirmAccountCreation]: confirmAccountCreation.Interactor;
  [UseCases.editUserPreferences]: editUserPreferences.Interactor;
  [UseCases.retrieveUserPreferences]: retrieveUserPreferences.Interactor;
  [UseCases.retrievePersonalWeeklyCalendar]: retrievePersonalWeeklyCalendar.Interactor;
  [UseCases.editPersonalWeeklyCalendar]: editPersonalWeeklyCalendar.Interactor;
  [UseCases.changePassword]: changePassword.Interactor;
  [UseCases.inviteMembers]: inviteMembers.Interactor;
  [UseCases.fillAccount]: fillAccount.Interactor;
  [UseCases.assignPersonalCalendarHolidays]: assignPersonalCalendarHolidays.Interactor;
  [UseCases.retrievePersonalCalendarHolidays]: retrievePersonalCalendarHolidays.Interactor;
  [UseCases.unAssignPersonalCalendarHolidays]: unAssignPersonalCalendarHolidays.Interactor;
}

type Boundaries = BoundaryMap<Core>;

type Dependencies = {
  gatekeeper: Gatekeeper;
  session: Session;
  tokenDecoder: TokenDecoder;
  repository: Repository;
  inviters: Inviters;
};

function createCore(dependencies: Dependencies): Core {
  return {
    [UseCases.authenticate]: authenticate.createInteractor(
      dependencies.gatekeeper,
      dependencies.session,
      dependencies.tokenDecoder,
      dependencies.repository,
    ),
    [UseCases.logOut]: logOut.createInteractor(dependencies.session),
    [UseCases.createAccount]: createAccount.createInteractor(
      dependencies.repository,
    ),
    [UseCases.confirmAccountCreation]: confirmAccountCreation.createInteractor(
      dependencies.repository,
    ),
    [UseCases.editUserPreferences]: editUserPreferences.createInteractor(
      dependencies.repository,
    ),
    [UseCases.retrieveUserPreferences]:
      retrieveUserPreferences.createInteractor(dependencies.repository),
    [UseCases.retrievePersonalWeeklyCalendar]:
      retrievePersonalWeeklyCalendar.createInteractor(dependencies.repository),
    [UseCases.editPersonalWeeklyCalendar]:
      editPersonalWeeklyCalendar.createInteractor(dependencies.repository),
    [UseCases.changePassword]: changePassword.createInteractor(
      dependencies.repository,
    ),
    [UseCases.inviteMembers]: inviteMembers.createInteractor(
      dependencies.inviters,
    ),
    [UseCases.fillAccount]: fillAccount.createInteractor(
      dependencies.repository,
    ),
    [UseCases.assignPersonalCalendarHolidays]:
      assignPersonalCalendarHolidays.createInteractor(dependencies.repository),
    [UseCases.retrievePersonalCalendarHolidays]:
      retrievePersonalCalendarHolidays.createInteractor(
        dependencies.repository,
      ),
    [UseCases.unAssignPersonalCalendarHolidays]:
      unAssignPersonalCalendarHolidays.createInteractor(
        dependencies.repository,
      ),
  };
}

export type { Core, Boundaries, Dependencies };

export { UseCases, createCore };
